'use strict';
// frontend/src/utils/partnershipMap.js
// Pure, framework-independent helpers for the admin Sites Map view
// (frontend/src/pages/Partnership.jsx -> MapPanel). Kept separate from the
// Leaflet-facing component so the actual data logic (which sites appear,
// what a popup shows, whether a coverage circle draws) is testable with
// plain Node assertions — no browser, no DOM, no Leaflet mock needed. See
// tests/partnership_map.test.js.
//
// CommonJS on purpose (not ES `export`) so the exact same file can be
// require()'d by a plain Node test script and imported by webpack/CRA from
// the React component — both module systems resolve module.exports fine.

const UGANDA_CENTER = { lat: 1.3733, lng: 32.2903 };
const DEFAULT_ZOOM = 7;

const STATUS_COLOR = {
  planned: '#9ca3af',
  funded: '#3b82f6',
  equipment_assigned: '#f59e0b',
  installed: '#06b6d4',
  live: '#22c55e',
  operating: '#16a34a',
};
const DEFAULT_STATUS_COLOR = '#9ca3af';

function statusColor(status) {
  return STATUS_COLOR[status] || DEFAULT_STATUS_COLOR;
}

// A site only ever appears on the map once it has BOTH coordinates recorded
// — no partial/guessed placement. Also guards against corrupt/out-of-range
// values slipping through (the schema already range-validates on write, but
// this keeps the map itself defensive regardless of data source).
function hasValidGps(site) {
  return (
    typeof site?.gpsLat === 'number' && Number.isFinite(site.gpsLat) &&
    typeof site?.gpsLng === 'number' && Number.isFinite(site.gpsLng) &&
    site.gpsLat >= -90 && site.gpsLat <= 90 &&
    site.gpsLng >= -180 && site.gpsLng <= 180
  );
}

function sitesWithGps(sites) {
  return (sites || []).filter(hasValidGps);
}

function hasCoverage(site) {
  return typeof site?.coverageRadius === 'number' && Number.isFinite(site.coverageRadius) && site.coverageRadius > 0;
}

// Only sites that are both mappable (valid GPS) and have a coverage radius
// get a circle drawn — a coverage circle with no anchor point is meaningless.
function sitesWithCoverageCircle(sites) {
  return sitesWithGps(sites).filter(hasCoverage);
}

// The exact fields a pin's popup shows, in a stable shape independent of
// whatever markup the Leaflet-facing component wraps it in.
function popupData(site) {
  return {
    name: site.name,
    subsiteCode: site.subsiteCode || null,
    status: site.status,
    projectName: site.project?.name || null,
    partnerName: site.partner?.name || null,
    activeCustomers: site.activeCustomers ?? 0,
    currentCapacity: site.currentCapacity ?? 0,
  };
}

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function popupHtml(site) {
  const d = popupData(site);
  const lines = [
    '<div class="dwi-map-popup">',
    `<div class="dwi-map-popup-title">${escapeHtml(d.name)}</div>`,
  ];
  if (d.subsiteCode) lines.push(`<div>Code: ${escapeHtml(d.subsiteCode)}</div>`);
  lines.push(`<div>Status: ${escapeHtml(d.status)}</div>`);
  lines.push(`<div>Project: ${escapeHtml(d.projectName || '—')}</div>`);
  lines.push(`<div>Partner: ${escapeHtml(d.partnerName || 'Unassigned')}</div>`);
  lines.push(`<div>${d.activeCustomers} / ${d.currentCapacity} customers</div>`);
  lines.push('</div>');
  return lines.join('');
}

module.exports = {
  UGANDA_CENTER,
  DEFAULT_ZOOM,
  STATUS_COLOR,
  DEFAULT_STATUS_COLOR,
  statusColor,
  hasValidGps,
  sitesWithGps,
  hasCoverage,
  sitesWithCoverageCircle,
  popupData,
  escapeHtml,
  popupHtml,
};
